import React, { useState, useRef, useEffect } from "react";
import { Search, ChevronDown, Check, X } from "lucide-react";
import ErrorMessage from "./ErrorMessage";

interface Option {
    value: string;
    label: string;
}

interface MultiSelectSearchProps {
    label?: string;
    error?: string;
    placeholder?: string;
    options: Option[];
    value?: string[];
    onChange: (value: string[]) => void;
    className?: string;
    disabled?: boolean;
    required?: boolean;
}

const MultiSelectSearch: React.FC<MultiSelectSearchProps> = ({
    label,
    error,
    placeholder = "Select options...",
    options = [],
    value = [],
    onChange,
    className,
    disabled = false,
    required = false
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const containerRef = useRef<HTMLDivElement>(null);

    const toggleDropdown = () => {
        if (!disabled) setIsOpen(!isOpen);
    };

    const handleClickOutside = (event: MouseEvent) => {
        if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
            setIsOpen(false);
        }
    };

    useEffect(() => {
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    useEffect(() => {
        if (isOpen) {
            setSearchTerm("");
        }
    }, [isOpen]);

    const filteredOptions = options.filter(option =>
        option.label.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handleSelect = (val: string) => {
        let newValue;
        if (value.includes(val)) {
            newValue = value.filter(v => v !== val);
        } else {
            newValue = [...value, val];
        }
        onChange(newValue);
    };

    const handleSelectAll = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (value.length === options.length) {
            onChange([]);
        } else {
            onChange(options.map(opt => opt.value));
        }
    };

    const clearAll = (e: React.MouseEvent) => {
        e.stopPropagation();
        onChange([]);
    };

    const removeOption = (val: string, e: React.MouseEvent) => {
        e.stopPropagation();
        onChange(value.filter(v => v !== val));
    };

    const getLabel = (val: string) => {
        return options.find(opt => opt.value === val)?.label || val;
    };

    return (
        <div className={`relative ${className || ""}`} ref={containerRef}>
            {label && (
                <label className="inline-block text-[14px] font-medium mb-1.5 text-gray-700">
                    {label}
                    {required && <span className="text-destructive ml-0.5">*</span>}
                </label>
            )}

            <div
                onClick={toggleDropdown}
                className={`flex min-h-[42px] w-full items-center justify-between rounded-md border border-gray-300 bg-white px-2 py-1 transition-all cursor-pointer ${disabled ? "opacity-50 cursor-not-allowed" : "hover:border-primary/50"
                    } ${isOpen ? "ring-[1px] ring-primary border-primary shadow-[0_0_0_1px_rgba(231,106,35,0.1)]" : "shadow-xs"}`}
            >
                <div className="flex flex-wrap gap-1.5 items-center flex-1">
                    {value.length > 0 ? (
                        value.map((v) => (
                            <div
                                key={v}
                                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-[#E9ECEF] text-[#495057] text-[13px] font-medium transition-colors"
                            >
                                <span className="max-w-[150px] truncate">{getLabel(v)}</span>
                                <X
                                    className="h-3.5 w-3.5 cursor-pointer hover:text-gray-900 transition-colors"
                                    onClick={(e) => removeOption(v, e)}
                                />
                            </div>
                        ))
                    ) : (
                        <span className="text-gray-400 text-[14px] ml-1">{placeholder}</span>
                    )}
                </div>
                <div className="flex items-center gap-1 pr-1 border-l border-gray-200 ml-2 py-1">
                    {value.length > 0 && (
                        <X
                            className="h-4 w-4 text-gray-400 hover:text-gray-600 transition-colors mx-1"
                            onClick={clearAll}
                        />
                    )}
                    <ChevronDown className={`h-4.5 w-4.5 text-gray-500 transition-transform duration-200 mx-1 ${isOpen ? "rotate-180" : ""}`} />
                </div>
            </div>

            {isOpen && (
                <div className="absolute z-50 mt-1 w-full rounded-md border border-gray-200 bg-white shadow-xl animate-in fade-in-0 zoom-in-95 overflow-hidden">
                    <div className="flex items-center border-b border-gray-100 px-3 py-2.5 bg-gray-50/50">
                        <Search className="mr-2.5 h-4 w-4 shrink-0 text-gray-400" />
                        <input
                            className="flex h-5 w-full bg-transparent text-[14px] outline-none placeholder:text-gray-400"
                            placeholder="Search..."
                            autoFocus
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <div className="max-h-[200px] overflow-y-auto py-1 scrollbar-thin scrollbar-thumb-gray-200">
                        {!searchTerm && (
                            <div
                                onClick={handleSelectAll}
                                className={`flex cursor-pointer select-none items-center px-4 py-2.5 text-[14px] font-medium transition-colors hover:bg-primary/5 ${value.length === options.length ? "text-primary" : "text-gray-700"
                                    }`}
                            >
                                {value.length === options.length ? "Deselect All" : "Select All"}
                            </div>
                        )}

                        {filteredOptions.length > 0 ? (
                            filteredOptions.map((option) => (
                                <div
                                    key={option.value}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleSelect(option.value);
                                    }}
                                    className={`relative flex cursor-pointer select-none items-center px-4 py-2.5 text-[14px] transition-colors ${value.includes(option.value)
                                        ? "bg-primary/5 text-primary font-medium"
                                        : "text-gray-600 hover:bg-gray-50"
                                        }`}
                                >
                                    <span className="flex-1">{option.label}</span>
                                    {value.includes(option.value) && (
                                        <Check className="h-4 w-4 text-primary shrink-0" strokeWidth={2.5} />
                                    )}
                                </div>
                            ))
                        ) : (
                            <div className="px-4 py-8 text-center text-[13px] text-gray-400 italic">
                                No results found.
                            </div>
                        )}
                    </div>
                </div>
            )}

            <ErrorMessage errors={error} />
        </div>
    );
};

export default MultiSelectSearch;
